/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Icon, Popup } from 'semantic-ui-react';

const iconStyle = { margin: 0, lineHeight: 1, height: '1em' };

// The one place the label is written, so the compact rendering, which has no room for a pill and shows
// only an icon, can prefix the same word onto its tooltip and the two say the same thing.
const SCHEDULED_LABEL = 'Scheduled';

/**
 * Marks a workspace that has a start/stop timetable set.
 *
 * A pill beside the status badge rather than an item in the meta row: a schedule phrase can run to a
 * week of differing hours, and the meta row is a single line of short facts. The label is the whole of
 * what the pill says and the timetable itself is the tooltip, because the detail is what a reader
 * scanning a list of cards does not need until they stop on one.
 *
 * Presentational: the caller decides whether a timetable is set and hands in the phrase describing it.
 *
 * @param {{ detail: string }} props - `detail` is the timetable as a phrase, with no `Scheduled:`
 *        prefix, since that would restate the label the tooltip hangs off.
 */
function ScheduledBadge({ detail }) {
  return (
    <Popup
      trigger={
        // No title attribute here: it renders the browser's own tooltip on top of the Popup.
        <span className="workspace-scheduled-badge">
          <Icon name="calendar alternate outline" style={iconStyle} />
          {SCHEDULED_LABEL}
        </span>
      }
      content={detail}
      size="small"
      position="top center"
      inverted
    />
  );
}

export default ScheduledBadge;
export { SCHEDULED_LABEL };
